import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useAuthModal } from '../context/AuthModalContext';
import { db } from '../firebase';
import { scrollToId } from '../utils/scroll';

const Cart = () => {
  const { items, removeItem, clearCart, subtotal } = useCart();
  const { user } = useAuth();
  const { openModal } = useAuthModal();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const goToVideos = () => {
    navigate('/');
    setTimeout(() => scrollToId('videos'), 100);
  };

  const handleCheckout = async () => {
    if (!user) {
      openModal('login');
      return;
    }

    setSubmitting(true);
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 12000)
      );
      await Promise.race([
        addDoc(collection(db, 'orders'), {
          userId: user.uid,
          userEmail: user.email,
          items: items.map(({ id, title, price }) => ({ id, title, price })),
          subtotal,
          status: 'pending',
          createdAt: serverTimestamp(),
        }),
        timeout,
      ]);
      setOrderPlaced(true);
      clearCart();
      toast.success("Order received! We'll email you to complete payment.");
    } catch (err) {
      toast.error("We couldn't submit your order. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-gray-50 py-16">
        <div className="container mx-auto px-6 lg:px-8 max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Your Cart</h1>
          <p className="text-gray-600 mb-10">Review the videos you've selected before checking out.</p>

          {orderPlaced ? (
            <div className="bg-white rounded-2xl shadow-md p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Order received!</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                We've received your order and will email you at{' '}
                <span className="font-medium">{user?.email}</span> with a secure payment link to
                complete your purchase.
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
              >
                Continue Browsing
              </Link>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-md p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
              <p className="text-gray-600 mb-8">Browse our premium animated videos and add your favorites.</p>
              <button
                type="button"
                onClick={goToVideos}
                className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
              >
                Browse Videos
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-2 space-y-4">
                <AnimatePresence>
                  {items.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4"
                    >
                      <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <video
                          src={item.src}
                          muted
                          loop
                          autoPlay
                          playsInline
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{item.title}</h3>
                        <p className="text-sm text-gray-500 truncate">{item.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-indigo-600">{item.price}</p>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="text-sm text-gray-400 hover:text-red-500 mt-1"
                        >
                          Remove
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="bg-white rounded-2xl shadow-md p-6 lg:sticky lg:top-24">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>
                <div className="flex justify-between text-gray-600 mb-2">
                  <span>
                    Subtotal ({items.length} item{items.length === 1 ? '' : 's'})
                  </span>
                  <span>${subtotal}</span>
                </div>
                <div className="border-t border-gray-100 my-4"></div>
                <div className="flex justify-between font-bold text-gray-900 mb-6">
                  <span>Total</span>
                  <span>${subtotal}</span>
                </div>
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? 'Submitting...' : user ? 'Checkout' : 'Log in to Checkout'}
                </button>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  We'll email you a secure payment link to complete your purchase.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Cart;
